/**
 * Lazy-loaded extra detail for a selected enrollment row on the admin Enrollments page.
 *
 * The list row only has space for a couple of dates, so everything an admin actually asks
 * for once they've found the row lives here: the enrollment window against the program
 * window, the schedule, waiver/level metadata, the *full* per-day date list (the row
 * truncates at 3), and — for `flex_monthly` — the upcoming months' day plans with their
 * lock status and estimated charge.
 *
 * Flex plans are the reason this is lazy: they're a separate table and only a handful of
 * programs use them, so we fetch them on expand rather than widening the list payload.
 * Mounted only while the row is expanded.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../supabase';
import { formatDateDisplay } from '../../utils/date';
import { getScheduleDescription, optInItemsOf, parseCron, selectedDaysOf } from '../../types/program';
import type { FlexMonthPlan } from '../../types/program';
import {
  computeFlexEstimate,
  countBillableDates,
  flexSummaryMonths,
  formatUpdatedBy,
  isPlanLocked,
  splitFlexPricing,
} from '../../utils/flexPlan';
import { useFlexPricingRows } from '../../hooks/useFlexPricingRows';
import { money } from '../utils/money';

export interface EnrollmentDetailEnrollment {
  id: string;
  enrollment_start_date: string | null;
  enrollment_end_date: string | null;
  is_active: boolean;
  metadata?: Record<string, unknown>;
  programs?: {
    id: string;
    name?: string;
    billing_type?: string;
    schedule_cron?: string;
    start_date?: string;
    end_date?: string;
    duration?: number;
    type?: string;
  };
}

function monthLabel(monthYmd: string): string {
  const [y, m] = monthYmd.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** `12/03` — compact day chip label for a long date list. */
function shortDay(ymd: string): string {
  return ymd.slice(5).replace('-', '/');
}


const PLAN_STATUS_CLASS: Record<string, string> = {
  draft: 'bg-blue-100 text-blue-800',
  locked: 'bg-amber-100 text-amber-800',
  billed: 'bg-green-100 text-green-800',
  skipped: 'bg-gray-100 text-gray-600',
};

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-2 text-xs">
      <span className="w-24 shrink-0 font-medium text-gray-500">{label}</span>
      <span className="min-w-0 flex-1 break-words text-gray-800">{children}</span>
    </div>
  );
}

function DayChips({ dates, tone = 'blue' }: { dates: string[]; tone?: 'blue' | 'gray' }) {
  const cls = tone === 'blue' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-700';
  return (
    <div className="flex flex-wrap gap-1">
      {dates.map((d) => (
        <span
          key={d}
          className={`inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-medium tabular-nums ${cls}`}
        >
          {shortDay(d)}
        </span>
      ))}
    </div>
  );
}

/**
 * Read-only flex plan summary for the coming months. The editor stays in the Edit modal
 * (AdminFlexPlanSection) — this is the at-a-glance view for the expanded row.
 */
function FlexPlanSummary({
  enrollmentId,
  programId,
  programSchedule,
  enrollmentStartDate,
  optInItems,
}: {
  enrollmentId: string;
  programId: string;
  programSchedule?: string;
  enrollmentStartDate: string | null;
  /** `line_item_configs` ids this family declined — excluded from the per-day rate. */
  optInItems: string[] | null;
}) {
  const months = flexSummaryMonths();

  const { data: plans, isLoading, error } = useQuery<FlexMonthPlan[]>({
    queryKey: ['flex-plans', enrollmentId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('flex_month_plans')
        .select('*')
        .eq('enrollment_id', enrollmentId)
        .order('plan_month', { ascending: true });
      if (error) throw error;
      return (data ?? []) as FlexMonthPlan[];
    },
  });

  const { data: flexPricing } = useFlexPricingRows(programId, programSchedule);
  const pricing = flexPricing
    ? (() => {
        const { perDayRate, monthlyAddOns } = splitFlexPricing(flexPricing.rows, { optIn: optInItems });
        return { perDay: perDayRate, addOns: monthlyAddOns };
      })()
    : undefined;

  if (isLoading) return <div className="text-xs text-gray-500">Loading flex plans…</div>;
  if (error) return <div className="text-xs text-red-600">Failed to load flex plans</div>;

  const byMonth = new Map((plans ?? []).map((p) => [p.plan_month.slice(0, 10), p]));
  const cadence = (parseCron(programSchedule || '').days || []).length;

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="text-xs font-semibold uppercase tracking-wide text-gray-700">
          Flex day plans
        </div>
        <div className="text-[11px] text-gray-500">
          {pricing ? `${money(pricing.perDay)}/day` : ''}
          {pricing && pricing.addOns > 0 ? ` + ${money(pricing.addOns)}/mo` : ''}
          {cadence > 0 ? ` · program runs ${cadence} day${cadence === 1 ? '' : 's'}/wk` : ''}
        </div>
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {months.map((m) => {
          const plan = byMonth.get(m);
          const dates = plan?.dates ?? [];
          const billable = countBillableDates(dates, enrollmentStartDate);
          const estimate = pricing
            ? computeFlexEstimate(billable, pricing.perDay, plan?.status === 'skipped' ? 0 : pricing.addOns)
            : null;
          const locked = isPlanLocked(m);
          return (
            <div key={m} className="rounded-md border border-gray-200 bg-white px-3 py-2">
              <div className="flex flex-wrap items-center justify-between gap-1">
                <span className="text-xs font-semibold text-gray-900">{monthLabel(m)}</span>
                <span
                  className={`rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${
                    plan ? PLAN_STATUS_CLASS[plan.status] ?? 'bg-gray-100 text-gray-600' : 'bg-gray-100 text-gray-500'
                  }`}
                >
                  {plan ? plan.status : 'no plan'}
                </span>
              </div>
              <div className="mt-1 text-xs text-gray-700">
                {plan ? (
                  <>
                    <span className="font-medium">{billable}</span> billable day
                    {billable === 1 ? '' : 's'}
                    {dates.length !== billable && (
                      <span className="text-gray-500"> ({dates.length} selected)</span>
                    )}
                    {estimate !== null && (
                      <span className="text-gray-500"> · est. {money(estimate)}</span>
                    )}
                  </>
                ) : (
                  <span className="text-gray-500">Not planned yet</span>
                )}
              </div>
              {dates.length > 0 && (
                <div className="mt-1">
                  <DayChips dates={dates} />
                </div>
              )}
              <div className="mt-1 text-[10px] uppercase tracking-wide text-gray-400">
                {locked ? 'Editing closed' : 'Editable'}
                {plan?.updated_by ? ` · by ${formatUpdatedBy(plan.updated_by)}` : ''}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function EnrollmentDetailPanel({ enrollment }: { enrollment: EnrollmentDetailEnrollment }) {
  const program = enrollment.programs;
  const billing = program?.billing_type;
  const meta = enrollment.metadata ?? {};
  const level = typeof meta.level === 'string' && meta.level.trim() ? meta.level : null;
  const waiver = meta.signed_waiver as { name?: string; date?: string } | undefined;
  // From metadata, like every other reader. This panel used to be handed a `selected_days`
  // that the page above it had copied out of metadata by hand; when that hydration was
  // dropped the panel would simply have shown a camp with no dates.
  const selectedDays = [...selectedDaysOf(enrollment)].sort();

  return (
    <div className="space-y-3">
      <div className="rounded-md border border-gray-200 bg-white px-3 py-2">
        <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-700">
          Enrollment
        </div>
        <div className="grid grid-cols-1 gap-1 sm:grid-cols-2 sm:gap-x-4">
          <Fact label="Enrolled for">
            {formatDateDisplay(enrollment.enrollment_start_date || '')} –{' '}
            {formatDateDisplay(enrollment.enrollment_end_date || '')}
          </Fact>
          <Fact label="Program runs">
            {program?.start_date ? formatDateDisplay(program.start_date) : '—'} –{' '}
            {program?.end_date ? formatDateDisplay(program.end_date) : '—'}
          </Fact>
          <Fact label="Schedule">
            {getScheduleDescription(program?.schedule_cron || '')}
            {program?.duration ? ` · ${program.duration} min` : ''}
          </Fact>
          <Fact label="Billing">
            {billing === 'recurring_monthly' ? 'Monthly' :
             billing === 'fixed_term' ? 'Fixed term' :
             billing === 'per_day' ? 'Per day' :
             billing === 'flex_monthly' ? 'Flex monthly' : '—'}
          </Fact>
          {level && <Fact label="Level">{level}</Fact>}
          <Fact label="Waiver">
            {waiver?.name
              ? `Signed by ${waiver.name}${waiver.date ? ` on ${formatDateDisplay(waiver.date)}` : ''}`
              : 'Not signed'}
          </Fact>
        </div>
      </div>

      {billing === 'flex_monthly' && program?.id && (
        <FlexPlanSummary
          enrollmentId={enrollment.id}
          programId={program.id}
          programSchedule={program.schedule_cron}
          enrollmentStartDate={enrollment.enrollment_start_date}
          optInItems={optInItemsOf(enrollment)}
        />
      )}

      {billing === 'per_day' && selectedDays.length > 0 && (
        <div className="rounded-md border border-gray-200 bg-white px-3 py-2">
          <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-700">
            Selected days ({selectedDays.length})
          </div>
          <DayChips dates={selectedDays} />
        </div>
      )}
    </div>
  );
}

export default EnrollmentDetailPanel;
